import { transformProduct } from '@/common/dto/catalog';
import {
  transformBenefitCategoryDetail,
  transformBenefitCategoryList,
  transformBenefitOptin,
} from '@/common/integrations/menhir/employee/benefits/benefits.dto';
import {
  IBenefit,
  IBenefitDetail,
  IMenhirBenefit,
  IMenhirBenefitDetail,
  IMenhirBenefitOptin,
  IMenhirBenefitSlug,
} from '@/common/integrations/menhir/employee/benefits/benefits.types';
import { TOrderCategorySlug } from '@/common/integrations/menhir/employee/orders/orders.types';
import { menhirClient } from '@/common/integrations/menhir/menhir.client';
import {
  MENHIR_ENDPOINTS,
  endpointFor,
} from '@/common/integrations/menhir/menhir.endpoints';
import { ICatalogProduct } from '@/common/types/catalog';
import { TMoney } from '@coverflex-tech/hypernova-utils';
import { IMenhirCatalogProductsList } from './benefits.types';

export const getBenefitsList = <
  T extends IMenhirBenefitSlug = IMenhirBenefitSlug,
>(): Promise<IBenefit<T>[]> =>
  menhirClient
    .get<
      null,
      { benefits: IMenhirBenefit[] }
    >(MENHIR_ENDPOINTS.EMPLOYEE.BENEFITS.ROOT)
    .then(
      (envelope) =>
        transformBenefitCategoryList(envelope.benefits).filter(
          (benefit) => benefit.products.length > 0
        ) as IBenefit<T>[]
    );

export const getBenefitDetail = <
  T extends IMenhirBenefitSlug = IMenhirBenefitSlug,
>(
  benefitSlug: T
): Promise<IBenefitDetail<T>> =>
  menhirClient
    .get<
      null,
      {
        benefit: IMenhirBenefitDetail;
      }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.BENEFITS.DETAIL, benefitSlug))
    .then(
      (envelope) =>
        transformBenefitCategoryDetail(envelope.benefit) as IBenefit<T>
    );

export const getBenefitOptin = () =>
  menhirClient
    .get<
      null,
      { opt_in: IMenhirBenefitOptin }
    >(MENHIR_ENDPOINTS.EMPLOYEE.BENEFITS.OPTIN)
    .then((envelope) => transformBenefitOptin(envelope.opt_in));

export const assignBenefitOptin = (amount: TMoney) =>
  menhirClient
    .put<null, { opt_in: { id: string } }>(
      MENHIR_ENDPOINTS.EMPLOYEE.BENEFITS.OPTIN,
      {
        opt_in: {
          benefits_amount: {
            amount: amount.amount,
            currency: amount.currency,
          },
        },
      }
    )
    .then((envelope) => envelope.opt_in?.id);

export const getBenefitAvailableProducts = <
  T extends TOrderCategorySlug,
  U extends ICatalogProduct<T> = ICatalogProduct<T>,
>(
  category: T
) =>
  menhirClient
    .get<null, IMenhirCatalogProductsList<T>>(
      endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.BENEFITS.PRODUCTS, category),
      {
        params: {
          pagination: 'no',
        },
      }
    )
    .then((envelope) => envelope.products.list.map(transformProduct) as U[]);
